/**
 * WheelSegment Component
 *
 * Individual segment of the spin wheel rendered using SVG.
 */

import { memo } from 'react';
import type { SegmentColor } from '../../types/jackpot';

interface WheelSegmentProps {
  index: number;
  totalSegments: number;
  prize: string;
  color: SegmentColor;
  isJackpot?: boolean;
  radius?: number;
}

/**
 * Get the fill color/gradient for a segment
 */
function getSegmentFill(color: SegmentColor, isJackpot?: boolean): string {
  if (isJackpot) {
    return 'url(#jackpot-gradient)';
  }

  const colorMap: Record<SegmentColor, string> = {
    blue: '#60A5FA',
    green: '#34D399',
    purple: '#A78BFA',
    orange: '#FB923C',
    red: '#F87171',
    gold: '#FFD700',
  };

  return colorMap[color];
}

/**
 * Calculate the path for a pie slice segment
 */
function getSegmentPath(
  index: number,
  totalSegments: number,
  radius: number,
  centerX: number,
  centerY: number
): string {
  const anglePerSegment = (2 * Math.PI) / totalSegments;
  const startAngle = index * anglePerSegment - Math.PI / 2; // Start from top
  const endAngle = startAngle + anglePerSegment;

  const x1 = centerX + radius * Math.cos(startAngle);
  const y1 = centerY + radius * Math.sin(startAngle);
  const x2 = centerX + radius * Math.cos(endAngle);
  const y2 = centerY + radius * Math.sin(endAngle);

  const largeArcFlag = anglePerSegment > Math.PI ? 1 : 0;

  return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
}

/**
 * Calculate text position for the prize label
 */
function getTextPosition(
  index: number,
  totalSegments: number,
  radius: number,
  centerX: number,
  centerY: number
): { x: number; y: number; rotation: number } {
  const anglePerSegment = (2 * Math.PI) / totalSegments;
  const midAngle = index * anglePerSegment - Math.PI / 2 + anglePerSegment / 2;

  // Position text at 65% of radius
  const textRadius = radius * 0.65;
  const x = centerX + textRadius * Math.cos(midAngle);
  const y = centerY + textRadius * Math.sin(midAngle);

  // Rotate text to follow wheel direction
  const rotation = (midAngle * 180) / Math.PI + 90;

  return { x, y, rotation };
}

export const WheelSegment = memo(function WheelSegment({
  index,
  totalSegments,
  prize,
  color,
  isJackpot = false,
  radius = 150,
}: WheelSegmentProps) {
  const centerX = 160;
  const centerY = 160;
  const path = getSegmentPath(index, totalSegments, radius, centerX, centerY);
  const textPos = getTextPosition(index, totalSegments, radius, centerX, centerY);
  const fill = getSegmentFill(color, isJackpot);

  return (
    <g>
      {/* Segment path */}
      <path
        d={path}
        fill={fill}
        stroke="#1e293b"
        strokeWidth="1"
        className={isJackpot ? 'animate-pulse' : ''}
      />

      {/* Prize text */}
      <text
        x={textPos.x}
        y={textPos.y}
        transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
        fill={isJackpot ? '#1e293b' : '#ffffff'}
        fontSize={isJackpot ? '8' : '10'}
        fontWeight="bold"
        textAnchor="middle"
        dominantBaseline="middle"
        className="select-none"
      >
        {isJackpot ? 'JP' : prize}
      </text>
    </g>
  );
});
